import {
  Avatar,
  Box,
  Chip,
  InputAdornment,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FolderOpen, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../common/components/PageHeader";
import { toast } from "../../../common/components/Toast";
import { SPACING } from "../../../common/constants/spacing";
import { useProjectsList } from "../../api/admin";
import { StatCard } from "../../shared/components/StatCard";

function Projects() {
  const { t } = useTranslation("admin_panel");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const pageSize = 20;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      if (searchTerm !== debouncedSearchTerm) {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  const { projects, totalCount, totalPages, error } = useProjectsList(
    currentPage,
    pageSize,
    debouncedSearchTerm,
  );

  useEffect(() => {
    if (error) {
      toast.error("Failed to load projects");
      console.error("Error fetching projects:", error);
    }
  }, [error]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <>
      <PageHeader
        title={t("projects.title")}
        description={t("projects.description")}
        appendActions={
          <Box sx={{ minWidth: 230 }}>
            <StatCard
              title={t("projects.totalProjects")}
              value={totalCount}
              icon={FolderOpen}
              color="#ed6c02"
            />
          </Box>
        }
      />

      <TextField
        fullWidth
        placeholder={t("projects.searchPlaceholder")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={20} />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 500, mb: SPACING.sm }}
      />

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell align="right">Members</TableCell>
              <TableCell align="right">Assets</TableCell>
              <TableCell align="right">Sources</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.length > 0 ? (
              projects.map((project) => (
                <TableRow key={project.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={project.icon}
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "#ed6c02",
                          fontSize: 16,
                        }}
                      >
                        {project.name?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {project.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {project.owner ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={project.owner.avatar}
                          sx={{ width: 28, height: 28, fontSize: 12 }}
                        >
                          {project.owner.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="bodySm">
                            {project.owner.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {project.owner.email}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Chip label="No Owner" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="bodySm">
                      {project.member_count}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="bodySm">
                      {project.asset_count}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="bodySm">
                      {project.source_count}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="bodySm">
                      {formatDate(project.created_at)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    {debouncedSearchTerm
                      ? "No projects found matching your search"
                      : "No projects found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </>
  );
}

export default Projects;
